import React, { createContext, useContext, useEffect, useState } from "react";
import { connect } from "socket.io-client";
import frontendConfig from "../../../config/frontend";

const SocketContext = createContext(null);

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

export const useWebsocket = () => useContext(SocketContext);

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  // Read the stored login on the first render, not in an effect: starting from
  // null opened a viewer socket that was closed again mid-handshake as soon as
  // the stored user loaded ("WebSocket is closed before the connection is
  // established").
  const [user, setUser] = useState(readStoredUser);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event?.key === "user") {
        setUser(readStoredUser());
      }
    };

    window.addEventListener("storage", handleStorageChange); // Listen for storage changes

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Without a login, connect as a viewer. The backend accepts this token for
  // read-only listeners (routes/websocket). It is what lets a timeline output
  // nobody is logged into -- a broadcaster's capture browser feeding an SRT
  // stream -- receive live pause/play and goal alarms at all; before, only a
  // logged-in browser got a socket.
  const VIEWER_TOKEN = "VIEWER_GENERIC_TOKEN";

  // Keyed on the token, not the user object: re-reading the same login (a
  // storage event from another tab) must not drop a working connection.
  const token = user?.token || VIEWER_TOKEN;

  useEffect(() => {
    if (token) {
      console.log("Attempting to connect socket...");
      // Extract hostname from the backendConfig.apiUrl
      const url = new URL(frontendConfig.apiUrl);
      const hostname = url.hostname;
      const port = url.port;

      // Construct the WebSocket URL
      const wsProtocol = url.protocol === "https:" ? "wss" : "ws";
      const wsUrl = port
        ? `${wsProtocol}://${hostname}:${port}?token=${token}`
        : `${wsProtocol}://${hostname}?token=${token}`;
      const newSocket = connect(wsUrl, {
        transports: ["websocket"],
      });

      newSocket.on("connect", () => console.log("Socket connected"));
      newSocket.on("disconnect", () => console.log("Socket disconnected"));

      setSocket(newSocket);

      // Logging in or out swaps the token, so the old connection has to go.
      return () => {
        newSocket.close();
      };
    } else {
      socket?.disconnect();
      setSocket(null);
    }
  }, [token]);

  const login = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = (history) => {
    localStorage.removeItem("user");
    setUser(null);
    history?.push("/backend/login");
  };

  return (
    <SocketContext.Provider value={{ socket, user, login, logout }}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketContext, SocketProvider };
